import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { POLICY_VERSION } from '../../lib/policy';

export default function ExternalPrivacyPolicy() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-white">
      <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 active:scale-[0.99] transition-all"
            aria-label="חזרה"
          >
            <ArrowRight className="h-5 w-5" />
            חזרה
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-center">מדיניות פרטיות – LoyalFlow</h1>
        <p className="text-sm text-gray-500 mb-8 text-center">
          עודכן: 12.10.2025 | גרסה: {POLICY_VERSION}
        </p>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p>
            ב-LoyalFlow אנו מחויבים להגן על פרטיות המשתמשים שלנו. מדיניות זו מסבירה כיצד אנו אוספים,
            משתמשים ומגנים על המידע האישי שלך.
          </p>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">1. מידע שאנו אוספים</h2>
            <p className="mb-2">כאשר אתה משתמש בשירותי LoyalFlow, אנו עשויים לאסוף:</p>
            <ul className="list-disc mr-6 space-y-2">
              <li>מידע אישי: שם, כתובת אימייל, מספר טלפון</li>
              <li>מידע על תורים: תאריכים, שירותים שנבחרו, העדפות</li>
              <li>מידע טכני: כתובת IP, סוג דפדפן, מכשיר</li>
              <li>עוגיות (Cookies): לשיפור חוויית המשתמש</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">2. שימוש במידע</h2>
            <p className="mb-2">אנו משתמשים במידע שנאסף למטרות הבאות:</p>
            <ul className="list-disc mr-6 space-y-2">
              <li>לספק ולשפר את השירותים שלנו</li>
              <li>לנהל תורים ולשלוח תזכורות</li>
              <li>לתקשר איתך בנוגע לשירותים</li>
              <li>לשפר את חוויית המשתמש באתר</li>
              <li>לנתח שימוש ולשפר את המערכת</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">3. שיתוף מידע</h2>
            <p>
              אנו לא מוכרים או משכירים את המידע האישי שלך לצדדים שלישיים. אנו עשויים לשתף מידע רק:
            </p>
            <ul className="list-disc mr-6 space-y-2">
              <li>עם העסק שבו אתה מזמין תור (שם, טלפון, פרטי התור)</li>
              <li>עם ספקי שירותים טכניים (אחסון, תשלומים)</li>
              <li>כאשר נדרש על פי חוק</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">4. אבטחת מידע</h2>
            <p>
              אנו נוקטים באמצעי אבטחה מתקדמים להגנה על המידע שלך, כולל הצפנת SSL, גישה מוגבלת למידע,
              וגיבויים קבועים. עם זאת, אף שיטה אינה בטוחה ב-100%.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">5. עוגיות (Cookies)</h2>
            <p>
              אנו משתמשים בעוגיות לשיפור חוויית הגלישה, שמירת העדפות, וניתוח שימוש באתר.
              ניתן לנהל עוגיות דרך הגדרות הדפדפן.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">6. זכויותיך</h2>
            <p className="mb-2">בהתאם לחוק הגנת הפרטיות, זכותך:</p>
            <ul className="list-disc mr-6 space-y-2">
              <li>לבקש עותק של המידע שלך</li>
              <li>לבקש תיקון מידע שגוי</li>
              <li>לבקש מחיקת המידע שלך</li>
              <li>להתנגד לשימוש במידע שלך</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">7. שינויים במדיניות</h2>
            <p>
              אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים יפורסמו בעמוד זה, והמשך השימוש
              במערכת לאחר עדכון מהווה הסכמה למדיניות המעודכנת.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 text-gray-900">8. יצירת קשר</h2>
            <p>
              לשאלות או בקשות בנוגע לפרטיות, ניתן לפנות אלינו:{' '}
              <a href="mailto:privacy@loyalflow.co.il" className="text-blue-600 hover:underline">
                privacy@loyalflow.co.il
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
